import { type CSSProperties } from 'react';
import { useTheme } from '../context/ThemeContext';
import './skeleton.css';

// Cortex Skeleton (DESIGN_LANGUAGE_SPEC §25, §27: "skeleton loaders"). A theme-aware
// shimmer/pulse placeholder shown while data loads. Variants cover the common shapes:
// a text line, a block, and a circle (avatar/icon).

export type SkeletonVariant = 'text' | 'block' | 'circle';

export interface SkeletonProps {
  variant?: SkeletonVariant | undefined;
  /** CSS width (e.g. '100%', 200, '12rem'). */
  width?: number | string | undefined;
  /** CSS height (e.g. '1rem', 40). */
  height?: number | string | undefined;
  className?: string | undefined;
  /** Accessible label for the busy region. */
  label?: string | undefined;
}

const VARIANT_RADIUS: Record<SkeletonVariant, string> = {
  text: 'rounded-md',
  block: 'rounded-xl',
  circle: 'rounded-full',
};

function toCss(value: number | string | undefined): string | undefined {
  if (value === undefined) return undefined;
  return typeof value === 'number' ? `${value}px` : value;
}

export function Skeleton({
  variant = 'text',
  width,
  height,
  className,
  label = 'Loading',
}: SkeletonProps) {
  const { theme } = useTheme();
  const isDark = theme === 'dark';

  const defaultHeight =
    variant === 'text' ? '0.85rem' : variant === 'circle' ? '2.5rem' : '5rem';
  const defaultWidth = variant === 'circle' ? height ?? '2.5rem' : '100%';

  const style: CSSProperties = {
    width: toCss(width ?? defaultWidth),
    height: toCss(height ?? defaultHeight),
  };

  return (
    <span
      role="status"
      aria-busy="true"
      aria-label={label}
      data-testid="skeleton"
      style={style}
      className={`cortex-skeleton block ${VARIANT_RADIUS[variant]} ${
        isDark ? 'cortex-skeleton--dark' : 'cortex-skeleton--light'
      } ${className ?? ''}`}
    />
  );
}
